import React, { useEffect, useState } from 'react';
import AOS from 'aos';
import 'aos/dist/aos.css';
import 'animate.css';
import { NavLink, useLocation } from "react-router-dom";

const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    AOS.init({ duration: 1200, once: true });
  }, []);

  const goldColor = "#fc7303";
  const lightGold = "#F1E5AC";

  const doorCategories = ["Prelaminated", "Sunmica", "WPVC", "Grewing", "Teak", "All"];

  // Function to close menu when a link is clicked on mobile
  const closeMenu = () => setIsMenuOpen(false);

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Oswald:wght@400;600&family=Montserrat:wght@300;500;700&display=swap');

        :root {
          --light-gold: ${goldColor};
          --pale-gold: ${lightGold};
        }

        .main-navbar {
          background: #000000;
          transition: all 0.3s ease;
          position: sticky;
          top: 0;
          z-index: 999;
        }

        .main-navbar.glass {
          background: rgba(0, 0, 0, 0.85); 
          backdrop-filter: blur(12px);
          border-bottom: 1px solid rgba(255, 255, 255, 0.1);
        }

        /* --- DOOR ANIMATION --- */
        .door-container {
          perspective: 1200px;
          display: inline-block;
          margin-right: 15px;
          position: relative;
        }
        .door-container svg { overflow: visible; }
        .door-leaf {
          transform-origin: 15px center;
          transform-style: preserve-3d;
          fill: var(--light-gold);
          animation: initialDoorSwing 12s ease-in-out 1;
          transition: transform 0.7s cubic-bezier(0.4, 0, 0.2, 1), filter 0.4s ease, fill 0.4s ease;
        }

        @keyframes initialDoorSwing {
          0%   { transform: rotateY(0deg); }
          6%   { transform: rotateY(-75deg); }
          12%  { transform: rotateY(0deg); }
          18%  { transform: rotateY(-75deg); }
          24%  { transform: rotateY(0deg); }
          30%  { transform: rotateY(-75deg); }
          36%  { transform: rotateY(0deg); }
          42%  { transform: rotateY(-75deg); }
          48%  { transform: rotateY(0deg); }
          100% { transform: rotateY(0deg); }
        }

        .navbar-brand:hover .door-leaf {
          transform: rotateY(-75deg);
          filter: drop-shadow(4px 0 10px rgba(212,175,55,0.6));
        }
        .door-container path:first-child { transition: stroke 0.4s ease, filter 0.4s ease; }
        .navbar-brand:hover .door-container path:first-child {
          stroke: var(--light-gold);
          filter: drop-shadow(0 0 8px var(--light-gold));
        }
        .door-container circle { transition: all 0.4s ease; }
        .navbar-brand:hover .door-container circle {
          fill: var(--light-gold);
          filter: drop-shadow(0 0 6px var(--light-gold));
        }

        /* --- BRAND TITLE --- */
        .brand-title {
          font-family: 'Oswald', sans-serif;
          color: white !important;
          font-weight: 700;
          letter-spacing: 2px;
          text-transform: uppercase;
          display: inline-block;
          transition:
            color 0.3s ease,
            letter-spacing 0.3s ease,
            transform 0.3s ease;
          will-change: transform;
        }

        .navbar-brand:hover .brand-title {
          color: var(--light-gold) !important;
          letter-spacing: 2.5px;
          transform: translateY(-1px);
        }

        .brand-title::after {
          content: "";
          display: block;
          width: 0%;
          height: 2px;
          background: var(--light-gold);
          transition: width 0.4s ease;
          margin-top: 2px;
        }

        .navbar-brand:hover .brand-title::after {
          width: 100%;
        }

        .brand-subtitle {
          color: var(--light-gold) !important;
          font-size: 0.65rem;
          letter-spacing: 1.5px;
          font-weight: 700;
          transition: color 0.3s ease, letter-spacing 0.3s ease;
          text-transform: uppercase;
        }

        .navbar-brand:hover .brand-subtitle {
          color: white !important;
          letter-spacing: 2px;
        }

        /* --- NAV LINKS --- */
        .nav-item { position: relative; }
        .nav-link {
          color: #ffffff !important;
          font-size: 0.85rem;
          font-weight: 500;
          margin: 0 10px;
          transition: color 0.3s ease;
          text-decoration: none;
          position: relative;
          padding-bottom: 6px;
          display: inline-block;
        }

        .nav-link::after {
          content: "";
          position: absolute;
          width: 0;
          height: 2px;
          bottom: 0;
          left: 50%;
          transform: translateX(-50%);
          background-color: var(--light-gold);
          transition: width 0.3s ease;
        }

        .nav-link:hover::after,
        .nav-link.active-link::after {
          width: 100%;
        }

        .nav-link:hover { color: var(--light-gold) !important; }
        .active-link { color: ${goldColor} !important; }

        /* DROPDOWN LOGIC */
        @media (min-width: 1200px) {
          .nav-item:hover .dropdown-menu-custom {
            display: block;
          }
        }
        
        .dropdown-menu-custom {
          display: none;
          position: absolute;
          background: #111;
          top: 100%;
          left: 0;
          min-width: 200px;
          border-top: 3px solid var(--light-gold);
          box-shadow: 0 10px 30px rgba(0,0,0,0.5);
          list-style: none;
          padding: 10px 0;
          margin: 0;
          animation: fadeIn 0.3s ease;
        }

        .dropdown-item-custom {
          padding: 10px 20px;
          color: #fff;
          text-decoration: none;
          display: block;
          font-size: 0.7rem;
          letter-spacing: 1px;
          transition: 0.3s;
        }

        .dropdown-item-custom:hover {
          background: var(--light-gold);
          color: #000;
          padding-left: 25px;
          font-weight: bold;
        }

        @keyframes fadeIn {
          from { opacity: 0; transform: translateY(10px); }
          to { opacity: 1; transform: translateY(0); }
        }

        /* --- PHONE ICON --- */
        .phone-wrapper {
          border-left: 1px solid rgba(255, 255, 255, 0.1);
          padding-left: 25px;
          text-decoration: none;
          transition: 0.3s;
        }

        .gold-icon-circle {
          width: 45px; height: 45px;
          border: 1px solid var(--light-gold);
          border-radius: 50%;
          display: flex; align-items: center; justify-content: center;
          position: relative; transition: 0.4s;
        }

        .gold-icon-circle::before {
          content: ""; position: absolute; width: 100%; height: 100%;
          border: 1px solid var(--light-gold); border-radius: 50%;
          animation: pulseRing 2s infinite; z-index: -1;
        }

        @keyframes pulseRing {
          0% { transform: scale(1); opacity: 0.8; }
          100% { transform: scale(1.8); opacity: 0; }
        }

        .phone-wrapper:hover .gold-icon-circle {
          background: var(--light-gold);
          box-shadow: 0 0 20px var(--light-gold);
        }

        .wave { fill: none; stroke: ${goldColor}; stroke-width: 2; stroke-linecap: round; opacity: 0; animation: waveAnim 1.5s infinite; }
        .wave-1 { animation-delay: 0s; }
        .wave-2 { animation-delay: 0.3s; }
        .wave-3 { animation-delay: 0.6s; }
        @keyframes waveAnim { 0% { opacity: 0; } 50% { opacity: 1; } 100% { opacity: 0; } }
        .phone-wrapper:hover .wave { stroke: black; }
        .phone-wrapper:hover .phone-path { fill: black; }

        .hamburger-icon {
          display: flex;
          flex-direction: column;
          justify-content: space-around;
          width: 30px;
          height: 22px;
          background: transparent;
          border: none;
          cursor: pointer;
          padding: 0;
          z-index: 10;
        }

        .hamburger-icon span {
          width: 30px;
          height: 3px;
          background: var(--light-gold);
          border-radius: 10px;
          transition: all 0.4s cubic-bezier(0.68, -0.55, 0.265, 1.55);
          position: relative;
          transform-origin: 1px center;
        }

        .hamburger-icon.open span:nth-child(1) {
          transform: rotate(45deg) translate(0px, -2px);
          background: white;
        }
        .hamburger-icon.open span:nth-child(2) {
          opacity: 0;
          transform: translateX(20px);
        }
        .hamburger-icon.open span:nth-child(3) {
          transform: rotate(-45deg) translate(0px, 2px);
          background: white;
        }

        @media (max-width: 1199px) {
          .navbar-brand { max-width: 75%; }
          .brand-title { font-size: 1.1rem !important; }
          .brand-subtitle { font-size: 0.5rem !important; }
          
          .navbar-collapse {
            background: rgba(0, 0, 0, 0.95);
            padding: 1.5rem;
            border-radius: 10px;
            margin-top: 15px;
            border: 1px solid rgba(255, 255, 255, 0.1);
            text-align: center;
            display: none;
          }
          .navbar-collapse.show {
            display: block;
            animation: mobileMenuSlideDown 0.4s ease-out forwards;
          }

          @keyframes mobileMenuSlideDown {
            from { opacity: 0; transform: translateY(-15px); }
            to { opacity: 1; transform: translateY(0); }
          }
          
          .nav-item { margin-bottom: 15px; }

          .dropdown-menu-custom {
            position: relative;
            top: 0;
            width: 100%;
            border-top: none;
            border-left: 2px solid var(--light-gold);
            background: transparent;
            box-shadow: none;
            padding-bottom: 0;
            display: block; 
          }

          .dropdown-item-custom {
            font-size: 0.75rem;
            text-align: center;
            padding: 8px 10px;
          }

          .phone-wrapper {
            border-left: none;
            padding-left: 0;
            margin-top: 20px;
            justify-content: center;
            border-top: 1px solid rgba(255, 255, 255, 0.1);
            padding-top: 20px;
          }
        }
      `}</style>

      <nav className={`navbar navbar-expand-xl navbar-dark main-navbar ${scrolled ? "glass" : ""}`}>
        <div className="container-fluid px-lg-5">

          {/* BRAND LOGO */}
          <NavLink
            to="/"
            className="navbar-brand d-flex align-items-center"
            onClick={() => {
              closeMenu();
              if (location.pathname === "/") {
                window.scrollTo({ top: 0, behavior: "smooth" });
              }
            }}
          >
            <div className="door-container">
              <svg width="45" height="55" viewBox="0 0 100 120">
                <path d="M10 5V115H90V5H10Z" fill="none" stroke={goldColor} strokeWidth="4" />
                <path className="door-leaf" d="M15 10L85 5V115L15 110V10Z" />
                <circle cx="75" cy="60" r="4" fill="black" />
              </svg>
            </div>
            <div>
              <div className="brand-title h4 m-0">SREE RAGHAVENDRA</div>
              <div className="brand-subtitle">HARDWARE, PLYWOODS & DOORS</div>
            </div>
          </NavLink>

          {/* CUSTOM ANIMATED HAMBURGER BUTTON */}
          <button
            className="navbar-toggler shadow-none border-0 px-0"
            type="button"
            onClick={() => setIsMenuOpen(!isMenuOpen)}
            aria-expanded={isMenuOpen}
            aria-label="Toggle navigation"
          >
            <div className={`hamburger-icon ${isMenuOpen ? "open" : ""}`}>
              <span></span>
              <span></span>
              <span></span>
            </div>
          </button>

          {/* COLLAPSIBLE MENU */}
          <div className={`collapse navbar-collapse ${isMenuOpen ? "show" : ""}`} id="navbarNav">
            <ul className="navbar-nav mx-auto text-center">

              <li className="nav-item">
                <NavLink to="/" className={({ isActive }) => "nav-link " + (isActive ? "active-link" : "")} onClick={closeMenu}>
                  HOME
                </NavLink>
              </li>

              <li className="nav-item">
                <NavLink to="/about" className={({ isActive }) => "nav-link " + (isActive ? "active-link" : "")} onClick={closeMenu}>
                  ABOUT
                </NavLink>
              </li>

              {/* DOORS ITEM WITH EXISTING CATEGORY DROPDOWN */}
              <li className="nav-item">
                <NavLink
                  to="/doors"
                  className={({ isActive }) => "nav-link " + (isActive ? "active-link" : "")}
                  onClick={closeMenu}
                >
                  DOORS <span style={{ fontSize: '10px' }}>▼</span>
                </NavLink>

                <ul className="dropdown-menu-custom">
                  {doorCategories.map((cat) => (
                    <li key={cat}>
                      <NavLink to={`/doors?category=${cat}`} className="dropdown-item-custom" onClick={closeMenu}>
                        {cat.toUpperCase()} DOORS
                      </NavLink>
                    </li>
                  ))}
                </ul>
              </li>

              <li className="nav-item">
                <NavLink to="/contact" className={({ isActive }) => "nav-link " + (isActive ? "active-link" : "")} onClick={closeMenu}>
                  CONTACT US
                </NavLink>
              </li>

            </ul>

            {/* PHONE SECTION - RECONCILED PHONE NUMBER */}
            <a href="tel:+918374771149" className="phone-wrapper d-flex align-items-center">
              <div className="gold-icon-circle me-3">
                <svg width="24" height="24" viewBox="0 0 24 24">
                  <path className="phone-path" fill={goldColor} d="M6.62,10.79C8.06,13.62 10.38,15.94 13.21,17.38L15.41,15.18C15.69,14.9 16.08,14.82 16.43,14.93C17.55,15.3 18.75,15.5 20,15.5A1,1 0 0,1 21,16.5V20A1,1 0 0,1 20,21A17,17 0 0,1 3,4A1,1 0 0,1 4,3H7.5A1,1 0 0,1 8.5,4C8.5,5.25 8.7,6.45 9.07,7.57C9.18,7.92 9.1,8.31 8.82,8.59L6.62,10.79Z" />
                  <path className="wave wave-1" d="M14 3.5c2 .5 3.5 2 4 4" />
                  <path className="wave wave-2" d="M16 1c3 1 5 3 6 6" />
                  <path className="wave wave-3" d="M18 -1.5c4 1.5 7 4.5 8 8.5" />
                </svg>
              </div>
              <div className="text-start">
                <div className="fw-bold" style={{ color: 'white', fontSize: '0.9rem' }}>83747 71149</div>
                <div className="text-secondary fw-bold" style={{ fontSize: '0.6rem', letterSpacing: '1px' }}>CALL US NOW</div>
              </div>
            </a>

          </div>
        </div>
      </nav>
    </>
  );
};

export default Navbar;
